import { createAsyncThunk } from "@reduxjs/toolkit";
import { SortDirection } from "@shared/models/general";
import { callDocumentsAPI } from "@documents/store/thunks/callDocumentsAPI";
import { ThunkApiConfig } from "@shared/models/redux";
import { openNotificationThunk } from "@store/Notification/thunks";
import { setHighlightedVideo } from "@dataRooms/store/ducks";
import { DocumentRow } from "@documents/models/dataGrid.models";
import { normalizeDocumentResponse } from "@documents/utils";
import { DocumentResponse } from "@documents/models/api.models";

/**
 * DataRooms-specific normalizer that preserves the shared Documents behaviour
 * but ensures videos always carry a usable publish date, so the grid does not
 * show "N/A" when the EST field is missing.
 */
const normalizeDataRoomsDocument = (document: DocumentResponse): DocumentRow => {
  const base = normalizeDocumentResponse(document);

  // For DataRooms we want videos to show a publish date in the grid whenever
  // the backend has *any* date for them. Some Opportunity documents only
  // populate uploadDate (and/or publishDateStr), so we fall back to those.
  const rawPublishDate =
    document.publishDateEST ?? document.publishDate ?? document.publishDateStr ?? document.uploadDate ?? null;

  return {
    ...base,
    publishDate: rawPublishDate,
  };
};

export const fetchEntityDocuments = createAsyncThunk<
  Record<string, DocumentRow[]>,
  {
    documentTypeIds: number[];
    entitledEntityIds: number[];
    sortDirection: SortDirection;
    sortKey: string;
  },
  ThunkApiConfig
>(
  "dataRooms/fetchEntityDocuments",
  async ({ documentTypeIds, entitledEntityIds, sortDirection, sortKey }, { rejectWithValue, dispatch }) => {
    try {
      const documents = await dispatch(
        callDocumentsAPI({
          documentLocation: "Opportunity",
          documentStatuses: ["P", "A", "W", "V"],
          documentTypes: documentTypeIds,
          entitledEntityIds: entitledEntityIds,
          includeVideos: true,
          sortDirection,
          sortKey,
          userId: null,
          readOnlyFlag: null,
        })
      );
      const highlightedVideo = documents.results.find((document) => document.videoIsHighlighted);

      if (highlightedVideo) {
        dispatch(
          setHighlightedVideo({
            videoToken: highlightedVideo.videoToken,
            videoDescription: highlightedVideo.videoDescription,
            videoId: highlightedVideo.videoId,
            videoTokenExpiration: highlightedVideo.videoTokenExpiration,
            title: highlightedVideo.title,
          })
        );
      }

      return documents.results.reduce(
        (result: Record<number, DocumentRow[]>, document) => ({
          ...result,
          [document.documentTypeId]: result[document.documentTypeId]
            ? [...result[document.documentTypeId], normalizeDataRoomsDocument(document)]
            : [normalizeDataRoomsDocument(document)],
        }),
        {}
      );
    } catch (e) {
      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: "Failed to load data",
        })
      );

      return rejectWithValue("Failed to load entity documents");
    }
  }
);
